import Loader from "@/components/loader/Loader.vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import {Document} from "@/models/documents/Document";
import {deleteFileByUrl, optimizePhotos, uploadImage, uploadImageAsync} from "@/utils/upload-image";
import {fileSizeToMB, validFileSize10MB} from "@/utils/fileSizeToMB";
import * as firebase from "firebase";
import {DocumentCategory} from "@/models/documents/DocumentCategory";
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {GalleryImage} from "@/models/GalleryImage";
import {waitResizedImage} from "@/utils/wait-resized-image";
import {v4 as uuidv4} from "uuid";

export default {
  name: "DocumentsView",
  components: {
    Loader,
    ValidationObserver,
    ValidationProvider,
    ConfirmDlg,
    GalleryPhotosUploadDlg
  },
  data() {
    return {
      loading: true,
      saveLoading: false,
      loadingImage: false,
      deleteLoading: false,
      selectedPdf: null,
      isInvalidFile: false,
      fileSelected: false,
      prevPdfUrl: null,
      imagePreviewUrl: require('@/assets/no-image.jpg'),
    }
  },
  computed: {
    document(): Document {
      return this.$store.state.documentsModule.document;
    },
    categories(): DocumentCategory[] {
      return this.$store.state.documentsModule.documentsCategories;
    },
    currentFacility() {
      return this.$store.state.authModule.currentFacility;
    },
    documentImage() {
      return this.document.imageUrl?.length ? this.document.imageUrl : this.imagePreviewUrl;
    }
  },
  async mounted() {
    await this.$store.dispatch("getDocument", this.$route.params.id);
    await this.$store.dispatch("getDocumentsCategories");
    this.loading = false;
  },
  methods: {
    routeToDocuments() {
      this.$router.push({
        name: "DocumentsList"
      })
    },
    async saveDocument() {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) return

      const promises = [];
      this.saveLoading = true

      const updateData = {
        name: this.document.name,
        categoryIds: this.document.categoryIds,
        categories: this.document.categories,
        description: this.document.description,
        isEnabled: this.document.isEnabled,
        dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date()),
        imageUrl: this.document.imageUrl,
        thumbnailUrl: this.document?.thumbnailUrl ?? this.document.imageUrl
      }

      if (this.selectedPdf) {
        const pdfPromise = this.uploadFile(this.selectedPdf, this.document.id)

        if (this.prevPdfUrl) await deleteFileByUrl(this.prevPdfUrl)

        promises.push(pdfPromise)
      }

      const documentPromise = firebase.firestore().collection("documents").doc(this.document.id).update(updateData)

      promises.push(documentPromise)

      await Promise.all(promises).then(async () => {
        this.selectedPdf = null
        await this.$store.dispatch("getDocument", this.$route.params.id)
        this.$toast.open({
          type: "success",
          message: this.$t("EditDocument.DocumentSuccessUpdated")
        });
      }).catch(error => {
        this.$toast.open({
          type: "error",
          message: this.$t("GeneralTranslations.UnhandledError")
        });
      }).finally(() => {
        this.saveLoading = false
      })

    },
    deleteDocument() {
      this.$refs.confirmationModal
        .open(null, this.$i18n.messages[this.$i18n.locale]['EditDocument.DeleteDocumentMessage'], false)
        .then(async (response) => {
          if (response && response.agree) {
            this.deleteLoading = true
            await this.$store.dispatch("deleteDocument", this.document).then(async () => {
              this.deleteLoading = false;
              this.$router.push("/documents");
            })
          }
        })
    },
    async saveImage(e) {
      this.saveLoading = true
      const file = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);
      const path = `${this.currentFacility}/documents/${this.document.id}/${uuidv4()}`
      const prevImagUrl = this.document.imageUrl;

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        this.saveLoading = false;
        return;
      }

      uploadImage({
        path, file,
        onStart: () => {
          this.loadingImage = true
        },
        onComplete: async storage => {
          const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
          const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

          try {
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
            this.document.imageUrl = uploadedImageUrl
            this.document.thumbnailUrl = resizedImageUrl
          } catch (e) {
            this.document.imageUrl = uploadedImageUrl;
            this.document.thumbnailUrl = uploadedImageUrl;
          }

          this.loadingImage = false
          this.saveLoading = false

          await firebase.firestore().collection("documents").doc(this.document.id).update({
            imageUrl: this.document.imageUrl,
            thumbnailUrl: this.document.thumbnailUrl
          }).then(async () => {
            await deleteFileByUrl(prevImagUrl).then(() => { console.log(`prev image deleted`) })
          })
        }
      })
    },
    onPdfSelect(event) {
      this.isInvalidFile = this.getExtension(event.target.files[0].name).toLowerCase() != 'pdf'
      if (!this.isInvalidFile) {
        this.fileSelected = true
        this.selectedPdf = event.target.files[0];
        this.prevPdfUrl = this.document.pdfUrl;
      }
      else {
        this.fileSelected = false
      }
    },
    getExtension(filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },
    async uploadFile(file, contentId) {
      if (!file) return;

      try {
        const storageRef = firebase.storage().ref()
        const filesRef = storageRef.child(this.currentFacility).child('documents')

        let type = file.extension || file.type;
        if (type === 'pdf') {
          type = file.type;
        }

        const uploadFileRef = filesRef.child(contentId).child(`${file.name}`)

        return await uploadFileRef.put(file, { contentType: type }).then(async () => {
          return await uploadFileRef.getDownloadURL().then(async (url) => {
            const update = { pdfUrl: url }
            return await firebase.firestore().collection("documents").doc(contentId).update(update)
          })
        })

      } catch (error) {
        this.saveLoading = true
        return;
      }
    },
    onCategoryChange() {
      this.document.categories = this.categories.filter(x => this.document.categoryIds.includes(x.id)).map(x => ({ id: x.id, name: x.name })) || []
    },
    openGalleryPhotos() {
      (this.$refs.GalleryPhotosUploadDlg as any).open();
    },
    uploadGalleryPhoto(image: GalleryImage) {
      this.document.imageUrl = image.image;
      this.documentImage = image.image;
      this.document.thumbnailUrl = image?.thumbnailUrl ?? image.image;
    },
  }
}